import React, { useState } from 'react';
import { Users, X } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import Pagination from './Pagination';

const STATUS_COLORS = {
  // Mirror-derived progress stages (from AuditProgress).
  no_cycle:           { bg: 'bg-gray-100 text-gray-700', dark: 'bg-gray-700 text-gray-300' },
  not_started:        { bg: 'bg-orange-100 text-orange-700', dark: 'bg-orange-900/40 text-orange-300' },
  collecting:         { bg: 'bg-blue-100 text-blue-700', dark: 'bg-blue-900/40 text-blue-300' },
  in_review:          { bg: 'bg-purple-100 text-purple-700', dark: 'bg-purple-900/40 text-purple-300' },
  complete:           { bg: 'bg-green-100 text-green-800', dark: 'bg-green-900/40 text-green-300' },
  // Legacy JPRO statuses (fallback for rows without mirror data).
  scheduled:          { bg: 'bg-gray-100 text-gray-700', dark: 'bg-gray-700 text-gray-300' },
  setup_requested:    { bg: 'bg-blue-100 text-blue-700', dark: 'bg-blue-900/40 text-blue-300' },
  files_selected:     { bg: 'bg-blue-100 text-blue-700', dark: 'bg-blue-900/40 text-blue-300' },
  files_requested:    { bg: 'bg-orange-100 text-orange-700', dark: 'bg-orange-900/40 text-orange-300' },
  partially_received: { bg: 'bg-orange-100 text-orange-700', dark: 'bg-orange-900/40 text-orange-300' },
  all_received:       { bg: 'bg-teal-100 text-teal-700', dark: 'bg-teal-900/40 text-teal-300' },
  review_complete:    { bg: 'bg-green-100 text-green-700', dark: 'bg-green-900/40 text-green-300' },
  completed:          { bg: 'bg-green-100 text-green-800', dark: 'bg-green-900/40 text-green-300' },
  paused:             { bg: 'bg-gray-200 text-gray-600', dark: 'bg-gray-600 text-gray-300' },
  unresponsive:       { bg: 'bg-red-100 text-red-700', dark: 'bg-red-900/40 text-red-300' },
};

const StatusBadge = ({ status, isDark }) => {
  const colors = STATUS_COLORS[status] || STATUS_COLORS.no_cycle;
  const label = status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${isDark ? colors.dark : colors.bg}`}>
      {label}
    </span>
  );
};

const AuditPropertyTable = ({ properties, pagination, onPageChange, onPropertyClick, onTransition, teamMembers = [], onBulkAssign }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [selected, setSelected] = useState(new Set());
  const [bulkAssignTo, setBulkAssignTo] = useState('');

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === properties.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(properties.map((p) => p.id)));
    }
  };
  const clearSelection = () => setSelected(new Set());

  const handleBulkAssign = () => {
    if (onBulkAssign && selected.size > 0) {
      onBulkAssign(Array.from(selected), bulkAssignTo || null);
      clearSelection();
      setBulkAssignTo('');
    }
  };

  const thClass = `px-3 py-3 text-left text-xs font-bold font-rasa uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`;
  const tdClass = `px-3 py-3 text-sm font-rasa ${isDark ? 'text-gray-300' : 'text-gray-700'}`;
  const rowClass = `border-b ${isDark ? 'border-gray-700/50 hover:bg-gray-800/50' : 'border-gray-100 hover:bg-gray-50'} cursor-pointer transition-colors`;

  return (
    <div>
      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className={`flex items-center gap-3 px-4 py-2.5 border-b ${isDark ? 'bg-blue-900/20 border-blue-800/50' : 'bg-blue-50 border-blue-200'}`}>
          <span className={`text-[13px] font-rasa font-bold ${isDark ? 'text-blue-200' : 'text-blue-700'}`}>
            {selected.size} selected
          </span>
          <select
            value={bulkAssignTo}
            onChange={(e) => setBulkAssignTo(e.target.value)}
            className={`text-[13px] font-rasa px-2 py-1 rounded border ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}
          >
            <option value="">Assign to...</option>
            {teamMembers.map((m) => (
              <option key={m.user_id || m.id} value={m.user_id || m.id}>
                {m.first_name ? `${m.first_name} ${m.last_name || ''}`.trim() : m.username} ({m.role})
              </option>
            ))}
          </select>
          <button
            onClick={handleBulkAssign}
            disabled={!bulkAssignTo}
            className="flex items-center gap-1.5 px-3 py-1 rounded text-[13px] font-rasa font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
          >
            <Users size={13} /> Assign
          </button>
          <button onClick={clearSelection} className={`p-1 rounded ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}>
            <X size={14} />
          </button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <th className={`px-2 py-3 w-8`}>
                <input
                  type="checkbox"
                  checked={properties.length > 0 && selected.size === properties.length}
                  onChange={toggleAll}
                  className="rounded border-gray-300"
                />
              </th>
              <th className={thClass}>Property</th>
              <th className={thClass}>Company</th>
              <th className={thClass}>Status</th>
              <th className={thClass}>Month</th>
              <th className={thClass}>Units</th>
              <th className={thClass}>Contact</th>
              <th className={thClass}>Assigned To</th>
              <th className={thClass}>Files</th>
              <th className={thClass}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {properties.length === 0 ? (
              <tr>
                <td colSpan={10} className={`px-4 py-8 text-center text-[13px] font-rasa ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  No properties found
                </td>
              </tr>
            ) : (
              properties.map((prop) => (
                <tr key={prop.id} className={`${rowClass} ${selected.has(prop.id) ? (isDark ? 'bg-blue-900/10' : 'bg-blue-50/50') : ''}`} onClick={() => onPropertyClick(prop)}>
                  <td className="px-2 py-3 w-8" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected.has(prop.id)}
                      onChange={() => toggleSelect(prop.id)}
                      className="rounded border-gray-300"
                    />
                  </td>
                  <td className={tdClass}>
                    <div className="font-medium">{prop.property_name}</div>
                    <div className={`text-xs font-rasa ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      {[prop.state, prop.management_company].filter(Boolean).join(' · ') || ''}
                    </div>
                  </td>
                  <td className={tdClass}>{prop.processor_company_name || '—'}</td>
                  <td className={tdClass}>
                    <StatusBadge status={prop.stage || prop.status} isDark={isDark} />
                    {prop.to_review > 0 && (
                      <div className={`text-[11px] font-rasa mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {prop.received}/{prop.to_review} received{prop.units_outstanding > 0 ? ` · ${prop.units_outstanding} out` : ''}
                      </div>
                    )}
                    {prop.stage === 'no_cycle' && prop.latest_cycle_year && (
                      <div className={`text-[11px] font-rasa mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        Last: {prop.latest_cycle_year}
                      </div>
                    )}
                  </td>
                  <td className={tdClass}>
                    {prop.audit_month ? new Date(2000, prop.audit_month - 1).toLocaleString('default', { month: 'short' }) : <span className={isDark ? 'text-gray-600' : 'text-gray-300'}>-</span>}
                  </td>
                  <td className={tdClass}>
                    {prop.qty_units ? (
                      <span title={`${prop.qty_affordable_units || 0} affordable`}>{prop.qty_units} <span className={`text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>({prop.qty_affordable_units || 0})</span></span>
                    ) : (
                      <span className={`text-[11px] ${isDark ? 'text-red-400/60' : 'text-red-300'}`}>No data</span>
                    )}
                  </td>
                  <td className={tdClass}>
                    {prop.primary_contact_email ? (
                      <span className={`text-[12px] truncate max-w-[140px] block ${isDark ? 'text-gray-400' : 'text-gray-500'}`} title={prop.primary_contact_email}>
                        {prop.primary_contact_email}
                      </span>
                    ) : (
                      <span className={`text-[11px] ${isDark ? 'text-red-400/60' : 'text-red-300'}`}>No contact</span>
                    )}
                  </td>
                  <td className={tdClass}>
                    {prop.assigned_to ? (
                      <span className={`text-[12px] px-2 py-0.5 rounded font-rasa ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                        {prop.assigned_to.first_name || prop.assigned_to.username}
                      </span>
                    ) : (
                      <span className={`text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Unassigned</span>
                    )}
                  </td>
                  <td className={tdClass}>
                    <div className="flex items-center gap-2">
                      <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                        <div
                          className="bg-blue-500 h-1.5 rounded-full transition-all"
                          style={{ width: `${prop.files_required > 0 ? (prop.files_received / prop.files_required) * 100 : 0}%` }}
                        />
                      </div>
                      <span className="text-xs whitespace-nowrap">{prop.files_received}/{prop.files_required}</span>
                    </div>
                  </td>
                  <td className={tdClass} onClick={(e) => e.stopPropagation()}>
                    {prop.valid_transitions && prop.valid_transitions.length > 0 && (
                      <select
                        className={`text-xs px-2 py-1 rounded border ${
                          isDark ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-600'
                        }`}
                        value=""
                        onChange={(e) => {
                          if (e.target.value) onTransition(prop.id, e.target.value);
                        }}
                      >
                        <option value="">Advance...</option>
                        {prop.valid_transitions.map((t) => (
                          <option key={t} value={t}>
                            {t.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && (
        <Pagination
          currentPage={pagination.current_page}
          totalPages={pagination.total_pages}
          totalCount={pagination.total_count}
          perPage={pagination.per_page || 20}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
};

export { StatusBadge };
export default AuditPropertyTable;
